import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  TwoFactorActivateResponse,
  TwoFactorSetupResponse,
  TwoFactorStatusResponse,
} from '../models/two-factor.models';

/**
 * API-Service für die Profil-Sicherheits-Seite — alles was der
 * eingeloggte User selbst an seiner 2FA-Konfiguration ändern kann.
 *
 * Login-Time-2FA (Step-2 verify, recovery) gehört nicht hierher,
 * sondern in den AuthService — das sind die einzigen 2FA-Endpoints,
 * die ohne JWT-Cookie aufgerufen werden.
 */
@Injectable({ providedIn: 'root' })
export class TwoFactorService {
  private readonly http = inject(HttpClient);

  /** Liefert den aktuellen 2FA-Status (für UI-Anzeige). */
  status(): Observable<TwoFactorStatusResponse> {
    return this.http.get<TwoFactorStatusResponse>('/auth/2fa/status');
  }

  /**
   * Initiiert den Setup-Flow: erzeugt ein neues Secret und liefert
   * QR-Code + Klartext-Secret. User scannt mit Authenticator-App,
   * gibt anschließend ersten Code via activate() ein.
   */
  setup(): Observable<TwoFactorSetupResponse> {
    return this.http.post<TwoFactorSetupResponse>('/auth/2fa/setup', {});
  }

  /**
   * Aktiviert 2FA mit dem ersten gültigen TOTP-Code. Antwort enthält
   * die 10 Klartext-Recovery-Codes — Frontend MUSS dem User die
   * Möglichkeit geben, sie zu speichern (Print/Download/Kopieren).
   */
  activate(code: string): Observable<TwoFactorActivateResponse> {
    return this.http.post<TwoFactorActivateResponse>('/auth/2fa/activate', { code });
  }

  /**
   * Deaktiviert 2FA. Verlangt das aktuelle Passwort als Re-Auth-Schritt.
   * Wirft 409, wenn die Rolle TOTP-Pflicht hat.
   */
  disable(passwort: string): Observable<void> {
    return this.http.post<void>('/auth/2fa/disable', { passwort });
  }

  /**
   * Generiert die Recovery-Codes neu — bestehende werden invalidiert.
   * Liefert die 10 neuen Klartext-Codes zurück.
   */
  regenerateRecoveryCodes(): Observable<TwoFactorActivateResponse> {
    return this.http.post<TwoFactorActivateResponse>('/auth/2fa/recovery-codes/regenerate', {});
  }
}
